import type { FC } from "react";
import React, { useMemo } from "react";

import cn from "classnames";

import { BUTTON_CELL_DISPLAY_NAME } from "./utils/core";
import { createTestIdForButtonCellElement } from "./utils/getTestId";
import { extractProps, getChildrenRecord } from "../../../utils/react";
import { ContentsView } from "../../Content";
import { UniCell } from "../../UniCell";

import type { ButtonCellProps } from "./types";

import classes from "./ButtonCell.styl";

export const BUTTON_CELL_TEST_ID =
    createTestIdForButtonCellElement("button-cell");

/**
 * Компонент кнопки, который отображает доступное действие в виде горизонтальной ячейки.
 *
 * Основан на обёртке uniCell и переиспользует динамические отступы, область нажатия и все состояния.
 * По умолчанию отображает contentsView, может принимать в себя любые компоненты в дополнительные слоты для удобного формирования наследников
 *
 * <a href="https://www.figma.com/design/64y1aDfl0lSSpek3sq1Lrt/%F0%9F%8D%AC-Cards-%26-Cells-%26-Views?node-id=2745-11595&m=dev" target="_blank">Спека</a>
 * @constructor
 */
export const ButtonCell: FC<ButtonCellProps> = (props) => {
    const {
        customVarsStyles,
        currentProps: {
            state = "enabled",
            children,
            prefix = BUTTON_CELL_DISPLAY_NAME,
            "data-tsid": tsid = BUTTON_CELL_TEST_ID,
            ...restProps
        },
    } = useMemo(() => extractProps(props, ["customVarsStyles"]), [props]);

    const { Label, Subtitle, Value, LeftAddon, RightAddon, BottomAddon } =
        useMemo(
            () =>
                getChildrenRecord(children, prefix, {
                    Label: {
                        required: true,
                    },
                    Unknown: {
                        skipElement: true,
                    },
                }),
            [children, prefix],
        );

    const buttonCellStyles = cn(classes["button-cell"], customVarsStyles);

    return (
        <UniCell
            data-uikit={prefix}
            {...restProps}
            state={state}
            data-tsid={tsid}
            customVarsStyles={buttonCellStyles}
        >
            <UniCell.Middle>
                <ContentsView>
                    <ContentsView.Row columnPriority="none">
                        <ContentsView.Content>{Label}</ContentsView.Content>
                        {Value && (
                            <ContentsView.Content alignmentX="end">
                                {Value}
                            </ContentsView.Content>
                        )}
                    </ContentsView.Row>
                    {Subtitle && (
                        <ContentsView.Row>
                            <ContentsView.Content>
                                {Subtitle}
                            </ContentsView.Content>
                        </ContentsView.Row>
                    )}
                </ContentsView>
            </UniCell.Middle>

            {LeftAddon && <UniCell.Left>{LeftAddon}</UniCell.Left>}

            {RightAddon && <UniCell.Right>{RightAddon}</UniCell.Right>}

            {BottomAddon && <UniCell.Bottom>{BottomAddon}</UniCell.Bottom>}
        </UniCell>
    );
};

ButtonCell.displayName = BUTTON_CELL_DISPLAY_NAME;

export default ButtonCell;
